/**
 * Shared interaction / hover vocabulary — QA sweep, 2026-08.
 *
 * QA logged 66 findings across 30 pages asking for pointer cursors and hover
 * feedback, repeatedly citing scmp.com as the reference. The cursor half is
 * handled globally in app/styles/globals.css (@layer base) — you do NOT need
 * TAP on a <button> or an <a href>. Use TAP only on a role="button" element.
 *
 * This file supplies the hover half. The vocabulary below is distilled from
 * what the ej-health / ejinsight / ej-tech / ej-monthly verticals already do
 * (32 files use group-hover:scale today; components/shared/main used none of
 * it), so adopting it makes the main HKEJ site consistent with the verticals
 * rather than inventing a new language.
 *
 * ---------------------------------------------------------------------------
 * TAILWIND v4 JIT CONSTRAINT — same rule as components/shared/main/layout.ts:
 * every string below is a COMPLETE literal. Never interpolate a colour, a
 * duration or a breakpoint into these. The scanner only emits classes it can
 * see as whole strings; an interpolated value compiles clean, emits nothing,
 * and the hover silently does not work.
 * ---------------------------------------------------------------------------
 *
 * Colour note: the hover colour is `var(--hkej-hover, #56917d)` — each vertical
 * sets `--hkej-hover` in its own layout, and the main site takes the fallback.
 * (This comment used to name CORE_COLORS.brandGreenDark #108a60, which has not
 * been the value since `.hkej-tap-title` was parameterised.) It is already
 * the hover colour in components/home/video-strip.tsx and the --green-dark var
 * used across components/account, components/video and components/auth. QA
 * asked for "link blue" by analogy with SCMP; brand green is the HKEJ
 * equivalent. If the client wants literal blue, change it in HOVER_TITLE,
 * HOVER_TITLE_SELF, HOVER_LINK and the .hkej-tap-title rule in globals.css.
 */

/**
 * Put on the <a> (or wrapper) that owns a hover interaction, so the plain-CSS
 * `.hkej-tap:hover .hkej-tap-title` / `.hkej-tap-zoom` rules in globals.css can
 * reach its descendants. Cursor is already handled globally for <a>/<button>.
 */
export const TAP_LINK = "hkej-tap";

/** Card / row wrapper that owns a Tailwind `group-hover:` interaction. */
export const TAP_GROUP = "group cursor-pointer";

/** A non-<button>, non-<a> element carrying role="button". */
export const TAP = "cursor-pointer";

/**
 * Headline / clickable text nested inside a TAP_LINK. Plain CSS (globals.css)
 * for the same reason as HOVER_IMAGE, and because card headlines almost always
 * carry an inline `color` that no Tailwind class could override anyway.
 */
export const HOVER_TITLE = "hkej-tap-title";

/** Same, on a self-hovering <a> with no group wrapper. */
/**
 * @deprecated Prefer `HOVER_TITLE` + `hkej-tap-self`.
 *
 * This hardcodes the main-site green as a Tailwind literal, so it CANNOT read
 * a vertical's `--hkej-hover` — using it inside a vertical paints that vertical
 * main-site green. It currently has no call sites; kept only so an import does
 * not break, and reading `--hkej-hover` with the same fallback so that if one
 * appears it is at least brand-aware.
 */
export const HOVER_TITLE_SELF =
  "transition-colors duration-200 ease-out hover:text-[color:var(--hkej-hover,#56917d)] motion-reduce:transition-none";

/** Standalone text link — footer, "更多 ›", breadcrumbs, contact lists.
 *  Keeps its underline: these are prose links, not card headlines. */
/**
 * Reads `--hkej-hover` rather than hardcoding the green, so a vertical adopting
 * it gets its own accent. Both current call sites are main-site, where the
 * fallback resolves to exactly the colour that was hardcoded — so this is a
 * no-op there and a trap removed for everyone else.
 */
export const HOVER_LINK =
  "transition-colors duration-200 ease-out hover:text-[color:var(--hkej-hover,#56917d)] hover:underline underline-offset-2 motion-reduce:transition-none";

/**
 * Image hover treatment for MAIN-SITE article cards: none.
 *
 * QA originally asked for a slight zoom ("這些縮圖在懸停時應該稍微放大一點") and
 * that is what `hkej-tap-zoom` did. The instruction was later reversed — the
 * main site's article items are to have "NO zoom in on the image" — so this is
 * now empty rather than deleted, which keeps every call site intact and makes
 * the decision revertible in one line.
 *
 * The verticals still zoom via Tailwind's own `group-hover:scale-*`; they are
 * not affected by this constant.
 */
export const HOVER_IMAGE = "";

/** Horizontal chevron, e.g. the "›" in a "更多 ›" link. */
export const HOVER_ARROW =
  "transition-transform duration-300 ease-out group-hover:translate-x-1 motion-reduce:transition-none";

/** Diagonal ↗ arrow — matches ejinsight/ej-tech section headers. */
export const HOVER_ARROW_DIAG =
  "transition-transform duration-300 ease-out group-hover:translate-x-0.5 group-hover:-translate-y-0.5 motion-reduce:transition-none";

/** Full-bleed list row tint. Same colour as the .s360-row rule in globals.css. */
export const HOVER_ROW =
  "transition-colors duration-150 ease-out hover:bg-[#f5f5f7] motion-reduce:transition-none";

/** Icon-only affordance — share rail, bookmark, social icons. */
export const HOVER_ICON =
  "transition-opacity duration-150 ease-out hover:opacity-60 motion-reduce:transition-none";

/**
 * Boundary / disabled button: grey, non-interactive, no hover.
 * Closes the "prev/first must grey out on page 1, next/last on the final page"
 * findings on /e-paper, /lj/epaper and the ej-tech carousels in one string.
 */
export const DISABLED_BTN =
  "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40 disabled:grayscale";
